import React from 'react';
import { motion } from 'framer-motion';
import { FaHome, FaTools, FaTree, FaPencilRuler, FaHardHat, FaWrench } from 'react-icons/fa';

function Services() {
  const mainServices = [
    {
      icon: <FaHome />,
      title: "General Contracting",
      description: "Full-service contracting for residential and commercial projects. We manage every aspect of your construction needs.",
      features: ["Project Management", "Quality Control", "Timeline Management", "Budget Optimization"]
    },
    {
      icon: <FaTools />,
      title: "Renovations & Remodels",
      description: "Transform your existing space into something extraordinary. From kitchens to full home renovations.",
      features: ["Kitchen Remodels", "Bathroom Updates", "Basement Finishing", "Interior Renovations"]
    },
    {
      icon: <FaPencilRuler />,
      title: "New Construction",
      description: "Building your dream from the ground up. Custom homes and commercial buildings designed to your specifications.",
      features: ["Custom Homes", "Commercial Buildings", "Architectural Design", "Permit Management"]
    }
  ];

  const additionalServices = [
    {
      icon: <FaTree />,
      title: "Outdoor Living",
      description: "Create your perfect outdoor oasis with custom decks, patios, and landscaping features."
    },
    {
      icon: <FaHardHat />,
      title: "Commercial Projects",
      description: "Specialized solutions for businesses, from office renovations to retail spaces."
    },
    {
      icon: <FaWrench />,
      title: "Maintenance",
      description: "Keep your property in top condition with our comprehensive maintenance services."
    }
  ];

  const processSteps = [
    {
      step: 1,
      title: "Consultation",
      description: "We meet to discuss your vision, requirements, and budget."
    },
    {
      step: 2,
      title: "Design & Planning",
      description: "Our team creates detailed plans and proposals for your project."
    },
    {
      step: 3,
      title: "Execution",
      description: "We bring your vision to life with quality craftsmanship."
    },
    {
      step: 4,
      title: "Final Review",
      description: "Ensuring everything meets our high standards and your expectations."
    }
  ];

  return (
    <div className="services-container">
      {/* Hero Section */}
      <motion.section 
        className="services-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="services-hero-content">
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Our Services
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.7 }}
          >
            Comprehensive construction and renovation services tailored to your needs
          </motion.p>
        </div>
      </motion.section>

      {/* Main Services Section */}
      <section className="main-services-section">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Core Services
        </motion.h2>
        <div className="services-grid">
          {mainServices.map((service, index) => (
            <motion.div
              key={index}
              className="service-card main-service"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              whileHover={{ y: -10 }}
            >
              <div className="service-icon">{service.icon}</div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <ul className="service-features">
                {service.features.map((feature, i) => (
                  <motion.li
                    key={i}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: (index * 0.2) + (i * 0.1) }}
                  >
                    {feature}
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Additional Services Section */}
      <section className="additional-services-section">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Additional Services
        </motion.h2>
        <div className="additional-services-grid">
          {additionalServices.map((service, index) => (
            <motion.div
              key={index}
              className="service-card additional-service"
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              whileHover={{ scale: 1.05 }}
            >
              <div className="service-icon">{service.icon}</div>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          Our Process
        </motion.h2>
        <div className="process-steps">
          {processSteps.map((step, index) => (
            <motion.div
              key={index}
              className="process-step"
              initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.3 }}
            >
              <div className="step-number">{step.step}</div>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <motion.section
        className="cta-section"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <h2>Ready to Start Your Project?</h2>
        <p>Contact us today for a free consultation</p>
        <motion.button
          className="cta-button"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Get Started
        </motion.button>
      </motion.section>
    </div>
  );
}

export default Services;